import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useUser, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, Users, Trophy, BarChart3, Plus, 
  Trash2, LogOut, Copy, Timer, Hash, X,
  ChevronDown, UserPlus, Calendar, Zap,
  TrendingUp, ChevronLeft, ChevronRight, Pencil, Check
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, LineChart, Line, Cell
} from "recharts";

const USER_COLORS = ["#D97706", "#0EA5E9", "#22C55E", "#8B5CF6", "#EC4899", "#F59E0B", "#14B8A6", "#EF4444"];

// Time helpers: seconds <-> hh:mm:ss
const secsToHMS = (totalSecs) => {
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
};

const hmsToSecs = (hms) => {
  const parts = hms.split(":").map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
};

const formatValue = (value, type) => {
  if (type === "time") return secsToHMS(value);
  return String(value);
};

// Helper: get date range for period
const getDateRange = (period, refDate) => {
  const d = new Date(refDate);
  if (period === "day") {
    const ds = d.toISOString().split("T")[0];
    return { start: ds, end: ds, label: d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) };
  }
  if (period === "week") {
    const dayOfWeek = d.getDay();
    const start = new Date(d); start.setDate(d.getDate() - dayOfWeek);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    return {
      start: start.toISOString().split("T")[0],
      end: end.toISOString().split("T")[0],
      label: `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${end.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
    };
  }
  // month
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return {
    start: start.toISOString().split("T")[0],
    end: end.toISOString().split("T")[0],
    label: d.toLocaleDateString("en-US", { month: "long", year: "numeric" })
  };
};

const navigateDate = (period, refDate, direction) => {
  const d = new Date(refDate);
  if (period === "day") d.setDate(d.getDate() + direction);
  else if (period === "week") d.setDate(d.getDate() + direction * 7);
  else d.setMonth(d.getMonth() + direction);
  return d;
};

// Full month calendar grid
const MonthGrid = ({ refDate, calendarData, userColorMap, getUserColor, onDayClick, selectedDay }) => {
  const year = refDate.getFullYear();
  const month = refDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date().toISOString().split("T")[0];
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  return (
    <div data-testid="month-calendar-grid">
      <div className="grid grid-cols-7">
        {["SUN","MON","TUE","WED","THU","FRI","SAT"].map((d,i) => (
          <div key={i} className="text-center font-mono text-sm text-[#8C8C89] py-2 bg-[#F5F3EF] border border-[rgba(0,0,0,0.1)]">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day, i) => {
          if (!day) return <div key={i} className="bg-[#FAF9F7] min-h-[90px] border border-[rgba(0,0,0,0.08)]" />;
          const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
          const dayData = calendarData?.days?.[dateStr];
          const isToday = dateStr === today;
          const isSelected = dateStr === selectedDay;
          const entries = dayData?.entries || [];
          // group by user
          const byUser = {};
          entries.forEach(e => {
            if (!byUser[e.user_id]) byUser[e.user_id] = { username: e.username, workouts: [], total: 0 };
            byUser[e.user_id].workouts.push(e);
            byUser[e.user_id].total += e.total;
          });
          return (
            <button
              key={i}
              onClick={() => onDayClick(dateStr)}
              className={`bg-white min-h-[90px] p-2 text-left transition-all hover:bg-[#F5F3EF] border border-[rgba(0,0,0,0.08)] ${isSelected ? "ring-2 ring-[#D97706] ring-inset bg-[#D97706]/5" : ""}`}
              data-testid={`cal-day-${dateStr}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className={`font-mono text-base ${isToday ? "bg-[#D97706] text-white w-7 h-7 flex items-center justify-center rounded-full font-medium" : "text-[#5C5C5A]"}`}>{day}</span>
                {dayData && <span className="font-mono text-sm text-[#8C8C89] font-medium">{dayData.total}</span>}
              </div>
              {Object.entries(byUser).slice(0, 3).map(([uid, data]) => (
                <div key={uid} className="flex items-center gap-1 mb-0.5">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getUserColor(uid) }} />
                  <span className="font-mono text-xs text-[#5C5C5A] truncate">{data.username}</span>
                  <span className="font-mono text-xs ml-auto flex-shrink-0 font-medium" style={{ color: getUserColor(uid) }}>{data.total}</span>
                </div>
              ))}
              {Object.keys(byUser).length > 3 && (
                <span className="font-mono text-[10px] text-[#8C8C89]">+{Object.keys(byUser).length - 3} more</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Week grid: 7-day columns
const WeekGrid = ({ refDate, activities, getUserColor, onDayClick }) => {
  const dayOfWeek = refDate.getDay();
  const weekStart = new Date(refDate);
  weekStart.setDate(refDate.getDate() - dayOfWeek);
  const today = new Date().toISOString().split("T")[0];

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d.toISOString().split("T")[0];
  });

  // Group activities by date
  const byDate = {};
  weekDays.forEach(d => { byDate[d] = []; });
  activities.forEach(a => {
    const d = a.logged_at.split("T")[0];
    if (byDate[d]) byDate[d].push(a);
  });

  return (
    <div className="grid grid-cols-7" data-testid="week-grid">
      {weekDays.map(dateStr => {
        const d = new Date(dateStr + "T12:00:00");
        const isToday = dateStr === today;
        const dayActivities = byDate[dateStr] || [];
        // group by user+workout
        const grouped = {};
        dayActivities.forEach(a => {
          const key = `${a.user_id}-${a.workout_name}`;
          if (!grouped[key]) grouped[key] = { user_id: a.user_id, username: a.username, workout: a.workout_name, total: 0, type: a.type };
          grouped[key].total += a.value;
        });
        return (
          <button key={dateStr} onClick={() => onDayClick(dateStr)} className={`text-left p-2.5 bg-white border border-[rgba(0,0,0,0.08)] min-h-[110px] transition-all hover:bg-[#F5F3EF] ${isToday ? "ring-2 ring-[#D97706] ring-inset bg-[#D97706]/5" : ""}`} data-testid={`week-day-${dateStr}`}>
            <div className={`font-mono text-sm mb-1 ${isToday ? "text-[#D97706] font-medium" : "text-[#8C8C89]"}`}>
              {d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase()}
            </div>
            <div className={`font-['VT323'] text-3xl mb-2 ${isToday ? "text-[#D97706]" : "text-[#1F1F1E]"}`}>{d.getDate()}</div>
            <div className="space-y-1">
              {Object.values(grouped).slice(0, 4).map((g, i) => (
                <div key={i} className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getUserColor(g.user_id) }} />
                  <span className="font-mono text-[10px] text-[#5C5C5A] truncate flex-1">{g.workout}</span>
                  <span className="font-mono text-xs flex-shrink-0 font-medium" style={{ color: getUserColor(g.user_id) }}>{g.total}</span>
                </div>
              ))}
              {Object.values(grouped).length > 4 && (
                <span className="font-mono text-[10px] text-[#8C8C89]">+{Object.values(grouped).length - 4}</span>
              )}
              {dayActivities.length === 0 && <span className="font-mono text-[9px] text-[#8C8C89]">—</span>}
            </div>
          </button>
        );
      })}
    </div>
  );
};

// Day detail: full log list with edit/delete + add log
const DayDetail = ({ activities, getUserColor, userId, isOwner, editingLog, editValue, editDate, editOriginalDate, setEditingLog, setEditValue, setEditDate, setEditOriginalDate, handleEditLog, handleDeleteLog, addLogOpen, setAddLogOpen, addLogWorkout, setAddLogWorkout, addLogValue, setAddLogValue, addLogDate, setAddLogDate, handleAddLogForDay, workouts, selectedDay }) => {

  // Group by user
  const byUser = {};
  activities.forEach(a => {
    if (!byUser[a.user_id]) byUser[a.user_id] = { username: a.username, logs: [] };
    byUser[a.user_id].logs.push(a);
  });

  const defaultDate = selectedDay || new Date().toISOString().split("T")[0];

  return (
    <div className="space-y-3" data-testid="day-detail">
      {/* Add Log Button / Form */}
      {!addLogOpen ? (
        <button onClick={() => { setAddLogOpen(true); setAddLogDate(defaultDate); }} className="w-full py-2 border border-dashed border-[#D97706]/40 text-[#D97706] font-mono text-xs hover:bg-[#D97706]/5 transition-colors flex items-center justify-center gap-1.5" data-testid="add-log-btn">
          <Plus className="w-3.5 h-3.5" />ADD LOG {selectedDay ? `FOR ${new Date(selectedDay + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
        </button>
      ) : (
        <div className="p-3 border border-[#D97706]/30 bg-[#D97706]/5 space-y-2" data-testid="add-log-form">
          <div className="flex items-center gap-2 flex-wrap">
            <select value={addLogWorkout} onChange={(e) => setAddLogWorkout(e.target.value)} className="flex-1 min-w-[120px] h-8 bg-white border border-[rgba(0,0,0,0.15)] text-[#1F1F1E] font-mono text-xs px-2 focus:outline-none focus:border-[#D97706]" data-testid="add-log-workout-select">
              <option value="">select workout</option>
              {workouts.map(w => <option key={w.id} value={w.id}>{w.name} ({w.type})</option>)}
            </select>
            {workouts.find(w => w.id === addLogWorkout)?.type === "time" ? (
              <Input type="text" value={addLogValue} onChange={(e) => setAddLogValue(e.target.value)} placeholder="hh:mm:ss" className="h-8 w-24 bg-white border-[rgba(0,0,0,0.15)] text-[#D97706] font-mono text-xs" data-testid="add-log-value-input" />
            ) : (
              <Input type="number" value={addLogValue} onChange={(e) => setAddLogValue(e.target.value)} placeholder="reps" className="h-8 w-20 bg-white border-[rgba(0,0,0,0.15)] text-[#D97706] font-mono text-xs" data-testid="add-log-value-input" />
            )}
            <Input type="date" value={addLogDate} onChange={(e) => setAddLogDate(e.target.value)} className="h-8 w-36 bg-white border-[rgba(0,0,0,0.15)] text-[#5C5C5A] font-mono text-xs" data-testid="add-log-date-input" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleAddLogForDay} className="flex-1 h-8 retro-btn-solid font-mono text-xs" data-testid="add-log-submit">LOG</button>
            <button onClick={() => { setAddLogOpen(false); setAddLogWorkout(""); setAddLogValue(""); setAddLogDate(""); }} className="h-8 px-3 border border-[rgba(0,0,0,0.15)] text-[#8C8C89] font-mono text-xs hover:text-red-500 transition-colors">CANCEL</button>
          </div>
        </div>
      )}

      {/* Activity List */}
      {activities.length === 0 ? (
        <p className="font-mono text-sm text-[#8C8C89] text-center py-6">NO ACTIVITY</p>
      ) : (
        <div className="space-y-4 max-h-[450px] overflow-y-auto">
          {Object.entries(byUser).map(([uid, data]) => {
            const color = getUserColor(uid);
            return (
              <div key={uid}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 border-2 flex items-center justify-center font-mono text-[10px]" style={{ borderColor: color, color }}>{data.username.slice(0, 2).toUpperCase()}</div>
                  <span className="font-mono text-xs font-medium" style={{ color }}>{data.username}</span>
                  <span className="font-mono text-[10px] text-[#8C8C89]">{data.logs.length} logs</span>
                </div>
                <div className="space-y-1.5 ml-8">
                  {data.logs.map(activity => {
                    const isEditing = editingLog === activity.id;
                    const logDate = activity.logged_at.split("T")[0];
                    return (
                      <div key={activity.id} className="p-2 bg-[#FAF9F7] border border-[rgba(0,0,0,0.06)] flex items-center justify-between group" data-testid={`activity-${activity.id}`}>
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                          <span className="font-mono text-sm text-[#5C5C5A]">{activity.workout_name}</span>
                          <span className="font-mono text-xs text-[#8C8C89]">
                            {new Date(activity.logged_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {isEditing ? (
                            <div className="flex items-center gap-1 flex-wrap">
                              {activity.type === "time" ? (
                                <Input type="text" value={editValue} onChange={(e) => setEditValue(e.target.value)} placeholder="hh:mm:ss" className="h-7 w-24 bg-white border-[rgba(0,0,0,0.15)] text-[#D97706] font-mono text-xs" autoFocus />
                              ) : (
                                <Input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} className="h-7 w-16 bg-white border-[rgba(0,0,0,0.15)] text-[#D97706] font-mono text-xs" autoFocus />
                              )}
                              <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} className="h-7 w-32 bg-white border-[rgba(0,0,0,0.15)] text-[#5C5C5A] font-mono text-[10px]" />
                              <button onClick={() => handleEditLog(activity.id)} className="p-1 text-[#22C55E] hover:bg-[#22C55E]/10 transition-colors"><Check className="w-3.5 h-3.5" /></button>
                              <button onClick={() => { setEditingLog(null); setEditValue(""); setEditDate(""); setEditOriginalDate(""); }} className="p-1 text-[#8C8C89] hover:text-red-500 transition-colors"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          ) : (
                            <>
                              <span className="font-['VT323'] text-lg" style={{ color }}>{formatValue(activity.value, activity.type)}</span>
                              <span className="font-mono text-[9px] text-[#8C8C89]">{activity.type === "reps" ? "reps" : ""}</span>
                              <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => { setEditingLog(activity.id); setEditValue(activity.type === "time" ? secsToHMS(activity.value) : String(activity.value)); setEditDate(logDate); setEditOriginalDate(logDate); }} className="p-0.5 text-[#8C8C89] hover:text-[#D97706]" data-testid={`edit-log-${activity.id}`}><Pencil className="w-3 h-3" /></button>
                                <button onClick={() => handleDeleteLog(activity.id)} className="p-0.5 text-[#8C8C89] hover:text-red-500" data-testid={`delete-log-${activity.id}`}><Trash2 className="w-3 h-3" /></button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const LobbyView = () => {
  const { lobbyId } = useParams();
  const navigate = useNavigate();
  const { username, userId, users, switchUser, addUser } = useUser();
  
  const [lobby, setLobby] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [workoutLeaderboards, setWorkoutLeaderboards] = useState({});
  const [comparisonData, setComparisonData] = useState([]);
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("leaderboard");
  
  const [workoutOpen, setWorkoutOpen] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [workoutForm, setWorkoutForm] = useState({ name: "", type: "reps", target: "" });
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [customValue, setCustomValue] = useState("");
  const [newUserName, setNewUserName] = useState("");
  const [leaderboardPeriod, setLeaderboardPeriod] = useState("month");
  const [submitting, setSubmitting] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [statsView, setStatsView] = useState("overview");
  const [h2hUserA, setH2hUserA] = useState("");
  const [h2hUserB, setH2hUserB] = useState("");
  
  // Date navigation
  const [refDate, setRefDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  // Editing
  const [editingLog, setEditingLog] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editOriginalDate, setEditOriginalDate] = useState("");
  // Add log for specific day
  const [addLogOpen, setAddLogOpen] = useState(false);
  const [addLogWorkout, setAddLogWorkout] = useState("");
  const [addLogValue, setAddLogValue] = useState("");
  const [addLogDate, setAddLogDate] = useState("");

  // User color map: assign colors to lobby members
  const userColorMap = useMemo(() => {
    const map = {};
    lobby?.members?.forEach((m, i) => { map[m.user_id] = USER_COLORS[i % USER_COLORS.length]; });
    return map;
  }, [lobby?.members]);

  const dateRange = useMemo(() => getDateRange(leaderboardPeriod, refDate), [leaderboardPeriod, refDate]);

  useEffect(() => {
    if (!userId) { navigate("/"); return; }
    fetchLobbyData();
  }, [lobbyId, userId]);

  useEffect(() => {
    if (lobby) {
      fetchWorkoutLeaderboards();
      fetchComparisonData();
      fetchActivitiesForPeriod();
      fetchCalendar();
      if (activeTab === "stats") fetchStatsData();
    }
  }, [leaderboardPeriod, lobby, refDate]);

  useEffect(() => {
    if (activeTab === "stats" && lobby) fetchStatsData();
  }, [activeTab]);

  useEffect(() => {
    if (selectedDay && lobby) fetchActivitiesForDay(selectedDay);
  }, [selectedDay]);

  const fetchLobbyData = async () => {
    try {
      const [lobbyRes, workoutsRes] = await Promise.all([
        axios.get(`${API}/lobbies/${lobbyId}?user_id=${userId}`),
        axios.get(`${API}/lobbies/${lobbyId}/workouts?user_id=${userId}`)
      ]);
      setLobby(lobbyRes.data);
      setWorkouts(workoutsRes.data);
      if (workoutsRes.data.length > 0) setSelectedWorkout(workoutsRes.data[0]);
    } catch (err) {
      toast.error("Failed to load lobby");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchActivitiesForPeriod = async () => {
    try {
      const res = await axios.get(`${API}/lobbies/${lobbyId}/activities?user_id=${userId}&start_date=${dateRange.start}&end_date=${dateRange.end}`);
      setActivities(res.data);
    } catch (err) { console.error("Failed to load activities"); }
  };

  const fetchActivitiesForDay = async (day) => {
    try {
      const res = await axios.get(`${API}/lobbies/${lobbyId}/activities?user_id=${userId}&date=${day}`);
      setActivities(res.data);
    } catch (err) { console.error("Failed to load activities"); }
  };

  const fetchCalendar = async () => {
    try {
      const month = `${refDate.getFullYear()}-${String(refDate.getMonth()+1).padStart(2,"0")}`;
      const res = await axios.get(`${API}/lobbies/${lobbyId}/calendar?user_id=${userId}&month=${month}`);
      setCalendarData(res.data);
    } catch (err) { console.error("Failed to load calendar"); }
  };

  const fetchWorkoutLeaderboards = async () => {
    try {
      const res = await axios.get(`${API}/lobbies/${lobbyId}/workout-leaderboards?user_id=${userId}&period=${leaderboardPeriod}&start_date=${dateRange.start}&end_date=${dateRange.end}`);
      setWorkoutLeaderboards(res.data);
    } catch (err) { console.error("Failed to load workout leaderboards"); }
  };

  const fetchComparisonData = async () => {
    try {
      const res = await axios.get(`${API}/lobbies/${lobbyId}/user-totals?user_id=${userId}&period=${leaderboardPeriod}&start_date=${dateRange.start}&end_date=${dateRange.end}`);
      if (res.data.by_user) {
        const comparison = Object.entries(res.data.by_user).map(([uid, data]) => ({
          username: data.username, total: data.total, ...data.by_workout
        }));
        setComparisonData(comparison);
      }
    } catch (err) { console.error("Failed to load comparison data"); }
  };

  const fetchStatsData = async () => {
    try {
      const res = await axios.get(`${API}/lobbies/${lobbyId}/stats?user_id=${userId}&period=${leaderboardPeriod}&start_date=${dateRange.start}&end_date=${dateRange.end}`);
      setStatsData(res.data);
      if (res.data.comparison?.length >= 2) {
        if (!h2hUserA) setH2hUserA(res.data.comparison[0].user_id);
        if (!h2hUserB) setH2hUserB(res.data.comparison[1].user_id);
      } else if (res.data.comparison?.length === 1) {
        if (!h2hUserA) setH2hUserA(res.data.comparison[0].user_id);
        if (!h2hUserB) setH2hUserB(res.data.comparison[0].user_id);
      }
    } catch (err) { console.error("Failed to load stats"); }
  };

  const handleAddWorkout = async (e) => {
    e.preventDefault();
    if (!workoutForm.name || !workoutForm.type) { toast.error("Name and type required"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/lobbies/${lobbyId}/workouts?user_id=${userId}`, {
        ...workoutForm, target: workoutForm.target ? parseInt(workoutForm.target) : null
      });
      setWorkouts([...workouts, res.data]);
      setWorkoutOpen(false);
      setWorkoutForm({ name: "", type: "reps", target: "" });
      if (!selectedWorkout) setSelectedWorkout(res.data);
      toast.success("Workout added!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add workout"); }
    finally { setSubmitting(false); }
  };

  const handleDeleteWorkout = async (workoutId) => {
    if (!confirm("Delete this workout?")) return;
    try {
      await axios.delete(`${API}/lobbies/${lobbyId}/workouts/${workoutId}?user_id=${userId}`);
      const nw = workouts.filter(w => w.id !== workoutId);
      setWorkouts(nw);
      if (selectedWorkout?.id === workoutId) setSelectedWorkout(nw[0] || null);
      toast.success("Workout deleted");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const handleQuickLog = async (value) => {
    if (!selectedWorkout) { toast.error("Select a workout first"); return; }
    try {
      const payload = { workout_id: selectedWorkout.id, value };
      if (selectedDay) {
        payload.logged_at = selectedDay + "T12:00:00.000Z";
      }
      await axios.post(`${API}/lobbies/${lobbyId}/log?user_id=${userId}`, payload);
      if (selectedDay) fetchActivitiesForDay(selectedDay);
      else fetchActivitiesForPeriod();
      fetchWorkoutLeaderboards();
      fetchComparisonData();
      fetchCalendar();
      toast.success(`Logged ${selectedWorkout.type === "time" ? secsToHMS(value) : value + " reps"}${selectedDay ? " for " + selectedDay : ""}!`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to log"); }
  };

  const handleCustomLog = async () => {
    let value;
    if (selectedWorkout?.type === "time" && customValue.includes(":")) {
      value = hmsToSecs(customValue);
    } else {
      value = parseInt(customValue);
    }
    if (!value || value <= 0) { toast.error("Enter a valid value"); return; }
    await handleQuickLog(value);
    setCustomValue("");
  };

  const handleAddLogForDay = async () => {
    if (!addLogWorkout || !addLogValue) { toast.error("Select workout and enter value"); return; }
    let val;
    if (addLogValue.includes(":")) {
      val = hmsToSecs(addLogValue);
    } else {
      val = parseInt(addLogValue);
    }
    if (!val || val <= 0) { toast.error("Enter a valid value"); return; }
    const dateToUse = addLogDate || selectedDay || new Date().toISOString().split("T")[0];
    try {
      await axios.post(`${API}/lobbies/${lobbyId}/log?user_id=${userId}`, {
        workout_id: addLogWorkout,
        value: val,
        logged_at: dateToUse + "T12:00:00.000Z"
      });
      setAddLogOpen(false); setAddLogWorkout(""); setAddLogValue(""); setAddLogDate("");
      if (selectedDay) fetchActivitiesForDay(selectedDay);
      else fetchActivitiesForPeriod();
      fetchWorkoutLeaderboards(); fetchComparisonData(); fetchCalendar();
      toast.success(`Logged ${val} for ${dateToUse}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to log"); }
  };

  const handleEditLog = async (logId) => {
    let val;
    if (editValue.includes(":")) {
      val = hmsToSecs(editValue);
    } else {
      val = parseInt(editValue);
    }
    if (!val || val <= 0) { toast.error("Enter a valid value"); return; }
    try {
      const payload = { value: val };
      if (editDate && editDate !== editOriginalDate) {
        payload.logged_at = editDate + "T12:00:00.000Z";
      }
      await axios.put(`${API}/lobbies/${lobbyId}/log/${logId}?user_id=${userId}`, payload);
      setEditingLog(null); setEditValue(""); setEditDate(""); setEditOriginalDate("");
      if (selectedDay) fetchActivitiesForDay(selectedDay);
      else fetchActivitiesForPeriod();
      fetchWorkoutLeaderboards(); fetchComparisonData();
      fetchCalendar();
      toast.success("Log updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to edit"); }
  };

  const handleDeleteLog = async (logId) => {
    if (!confirm("Delete this log?")) return;
    try {
      await axios.delete(`${API}/lobbies/${lobbyId}/log/${logId}?user_id=${userId}`);
      if (selectedDay) fetchActivitiesForDay(selectedDay);
      else fetchActivitiesForPeriod();
      fetchWorkoutLeaderboards(); fetchComparisonData();
      fetchCalendar();
      toast.success("Log deleted");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const handleLeaveLobby = async () => {
    const isOwner = lobby?.owner_id === userId;
    if (!confirm(isOwner ? "DELETE this lobby?" : "Leave lobby?")) return;
    try {
      await axios.delete(`${API}/lobbies/${lobbyId}/leave?user_id=${userId}`);
      toast.success(isOwner ? "Lobby deleted" : "Left lobby");
      navigate("/");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleRemoveMember = async (memberId, e) => {
    e.stopPropagation();
    if (!confirm("Remove member?")) return;
    try {
      await axios.delete(`${API}/lobbies/${lobbyId}/members/${memberId}?user_id=${userId}`);
      setLobby({ ...lobby, members: lobby.members.filter(m => m.user_id !== memberId) });
      toast.success("Member removed");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleMemberClick = (member) => {
    const sessionUser = users.find(u => u.username === member.username);
    if (sessionUser) {
      switchUser(sessionUser.id);
      toast.success(`Switched to ${member.username}`);
      fetchLobbyData(); fetchWorkoutLeaderboards(); fetchComparisonData();
    } else { toast.info(`Add "${member.username}" to session first`); }
  };

  const handleSwitchUser = (user) => {
    switchUser(user.id);
    toast.success(`Switched to ${user.username}`);
    fetchLobbyData(); fetchWorkoutLeaderboards();
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserName.trim() || newUserName.trim().length < 2) { toast.error("Username must be at least 2 characters"); return; }
    setSubmitting(true);
    try {
      await addUser(newUserName.trim());
      toast.success(`Added ${newUserName.trim()}!`);
      setNewUserName(""); setAddUserOpen(false);
    } catch (err) { toast.error("Failed to add user"); }
    finally { setSubmitting(false); }
  };

  const copyLobbyId = () => { navigator.clipboard.writeText(lobbyId); toast.success("Lobby ID copied!"); };

  const handlePeriodChange = (p) => {
    setLeaderboardPeriod(p);
    setRefDate(new Date());
    setSelectedDay(null);
  };

  const handleDateNav = (dir) => {
    setRefDate(navigateDate(leaderboardPeriod, refDate, dir));
    setSelectedDay(null);
  };

  const handleDayClick = (day) => {
    setSelectedDay(selectedDay === day ? null : day);
  };

  const isOwner = lobby?.owner_id === userId;
  const quickReps = [5, 10, 20, 50];
  const quickTime = [30, 60, 120, 300];
  const quickValues = selectedWorkout?.type === "time" ? quickTime : quickReps;

  const getUserColor = (uid) => userColorMap[uid] || "#8C8C89";

  if (loading) {
    return (<div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center"><div className="font-mono text-sm text-[#8C8C89]">LOADING...</div></div>);
  }

  return (
    <div className="min-h-screen bg-[#FAF9F7] p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="flex items-center justify-between mb-6 pb-4 border-b border-[rgba(0,0,0,0.1)]">
          <div className="flex items-center gap-4">
            <Link to="/" className="p-2 text-[#8C8C89] hover:text-[#D97706] transition-colors"><ArrowLeft className="w-5 h-5" /></Link>
            <div>
              <h1 className="font-['VT323'] text-2xl md:text-3xl text-[#D97706]">{lobby?.name}</h1>
              <button onClick={copyLobbyId} className="flex items-center gap-1 text-[#8C8C89] hover:text-[#5C5C5A] transition-colors">
                <span className="font-mono text-xs">ID: {lobbyId.slice(0, 8)}...</span><Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 bg-white border border-[rgba(0,0,0,0.1)] hover:border-[#D97706] transition-colors">
                  <div className="w-6 h-6 border-2 flex items-center justify-center font-mono text-xs" style={{ borderColor: getUserColor(userId), color: getUserColor(userId) }}>{username.slice(0, 2).toUpperCase()}</div>
                  <span className="font-mono text-xs text-[#1F1F1E] hidden sm:block">{username}</span>
                  <ChevronDown className="w-3 h-3 text-[#8C8C89]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48 bg-white border-[rgba(217,119,6,0.3)]" align="end">
                <div className="px-2 py-1.5"><span className="font-mono text-xs text-[#8C8C89]">SWITCH USER</span></div>
                {users.map((user) => (
                  <DropdownMenuItem key={user.id} onClick={() => handleSwitchUser(user)} className={`font-mono text-xs cursor-pointer ${user.id === userId ? 'bg-[#D97706]/10' : ''}`}>
                    <div className="w-5 h-5 border flex items-center justify-center text-[10px] mr-2" style={{ borderColor: getUserColor(user.id), color: getUserColor(user.id) }}>{user.username.slice(0, 2).toUpperCase()}</div>
                    <span style={{ color: getUserColor(user.id) }}>{user.username}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator className="bg-[rgba(0,0,0,0.08)]" />
                <DropdownMenuItem onClick={() => setAddUserOpen(true)} className="font-mono text-xs text-[#D97706] cursor-pointer"><UserPlus className="w-3 h-3 mr-2" />Add User</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button onClick={handleLeaveLobby} className="p-2 text-[#8C8C89] hover:text-red-500 transition-colors" title={isOwner ? "Delete Lobby" : "Leave Lobby"}><LogOut className="w-5 h-5" /></button>
          </div>
        </header>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-3 space-y-4">
            {/* Workouts */}
            <div className="retro-box p-4">
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-[rgba(0,0,0,0.08)]">
                <span className="font-mono text-xs uppercase text-[#8C8C89]">WORKOUTS</span>
                <Dialog open={workoutOpen} onOpenChange={setWorkoutOpen}>
                  <DialogTrigger asChild><button className="p-1 text-[#8C8C89] hover:text-[#D97706] transition-colors"><Plus className="w-4 h-4" /></button></DialogTrigger>
                  <DialogContent className="bg-white border-[rgba(217,119,6,0.3)] max-w-md">
                    <DialogHeader><DialogTitle className="font-['VT323'] text-2xl text-[#D97706]">ADD WORKOUT</DialogTitle></DialogHeader>
                    <form onSubmit={handleAddWorkout} className="space-y-4 mt-4">
                      <Input value={workoutForm.name} onChange={(e) => setWorkoutForm({ ...workoutForm, name: e.target.value })} placeholder="e.g., Push-ups" className="h-11 bg-[#FAF9F7] border-[rgba(0,0,0,0.15)] text-[#D97706] font-mono" />
                      <Select value={workoutForm.type} onValueChange={(v) => setWorkoutForm({ ...workoutForm, type: v })}>
                        <SelectTrigger className="h-11 bg-[#FAF9F7] border-[rgba(0,0,0,0.15)] text-[#1F1F1E] font-mono"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-[rgba(0,0,0,0.15)]">
                          <SelectItem value="reps" className="font-mono">REPS</SelectItem>
                          <SelectItem value="time" className="font-mono">TIME (sec)</SelectItem>
                        </SelectContent>
                      </Select>
                      <button type="submit" disabled={submitting} className="w-full h-11 retro-btn-solid font-mono text-sm">{submitting ? "ADDING..." : "[ADD]"}</button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="space-y-1 max-h-[180px] overflow-y-auto">
                {workouts.length === 0 ? (
                  <p className="font-mono text-xs text-[#8C8C89] text-center py-4">NO WORKOUTS</p>
                ) : workouts.map((workout) => (
                  <div key={workout.id} onClick={() => setSelectedWorkout(workout)} className={`p-2 cursor-pointer flex items-center justify-between group transition-all ${selectedWorkout?.id === workout.id ? 'bg-[#D97706]/10 border border-[#D97706]/30' : 'border border-transparent hover:bg-[#F5F3EF]'}`}>
                    <div className="flex items-center gap-2">
                      {workout.type === "reps" ? <Hash className="w-3 h-3 text-[#8C8C89]" /> : <Timer className="w-3 h-3 text-[#8C8C89]" />}
                      <span className={`font-mono text-sm ${selectedWorkout?.id === workout.id ? 'text-[#D97706]' : 'text-[#1F1F1E]'}`}>{workout.name}</span>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteWorkout(workout.id); }} className="p-1 text-[#8C8C89] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Log */}
            <div className="retro-box p-4">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[rgba(0,0,0,0.08)]">
                <Zap className="w-4 h-4 text-[#D97706]" />
                <span className="font-mono text-xs uppercase text-[#8C8C89]">QUICK LOG</span>
              </div>
              {selectedWorkout && <p className="font-mono text-xs text-[#D97706] mb-2">{selectedWorkout.name}{selectedDay && <span className="text-[#8C8C89]"> &middot; {new Date(selectedDay + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}</p>}
              {!selectedWorkout ? (
                <p className="font-mono text-xs text-[#8C8C89] text-center py-2">SELECT WORKOUT</p>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {quickValues.map((val) => (
                      <button key={val} onClick={() => handleQuickLog(val)} className="quick-btn py-2 text-sm" data-testid={`quick-log-${val}`}>
                        +{selectedWorkout.type === "time" ? secsToHMS(val) : val}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    {selectedWorkout.type === "time" ? (
                      <Input type="text" value={customValue} onChange={(e) => setCustomValue(e.target.value)} placeholder="hh:mm:ss" className="h-9 bg-[#FAF9F7] border-[rgba(0,0,0,0.1)] text-[#D97706] font-mono text-sm flex-1" data-testid="custom-time-input" />
                    ) : (
                      <Input type="number" value={customValue} onChange={(e) => setCustomValue(e.target.value)} placeholder="custom" className="h-9 bg-[#FAF9F7] border-[rgba(0,0,0,0.1)] text-[#D97706] font-mono text-sm flex-1" />
                    )}
                    <button onClick={handleCustomLog} className="px-3 h-9 retro-btn-solid font-mono text-xs">LOG</button>
                  </div>
                </div>
              )}
            </div>

            {/* Members */}
            <div className="retro-box p-4">
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-[rgba(0,0,0,0.08)]">
                <span className="font-mono text-xs uppercase text-[#8C8C89]">MEMBERS ({lobby?.members?.length || 0})</span>
              </div>
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {lobby?.members?.map((member) => {
                  const isInSession = users.some(u => u.username === member.username);
                  const isCurrentUser = member.user_id === userId;
                  const color = getUserColor(member.user_id);
                  return (
                    <div key={member.user_id} onClick={() => handleMemberClick(member)} className={`p-2 flex items-center justify-between group transition-all cursor-pointer ${isCurrentUser ? 'border border-opacity-30' : isInSession ? 'border border-transparent hover:bg-opacity-5' : 'border border-transparent hover:bg-[#F5F3EF]'}`} style={isCurrentUser ? { backgroundColor: color + "15", borderColor: color + "50" } : {}} title={isInSession ? `Click to switch to ${member.username}` : `Add ${member.username} to session first`}>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 border-2 flex items-center justify-center font-mono text-xs" style={{ borderColor: color, color }}>{member.username.slice(0, 2).toUpperCase()}</div>
                        <div>
                          <span className="font-mono text-sm" style={{ color }}>{member.username}</span>
                          <div className="flex items-center gap-1">
                            {member.user_id === lobby.owner_id && <span className="font-mono text-[10px] text-[#8C8C89]">owner</span>}
                            {isInSession && !isCurrentUser && <span className="font-mono text-[10px]" style={{ color: color + "99" }}>• in session</span>}
                          </div>
                        </div>
                      </div>
                      {isOwner && member.user_id !== userId && (
                        <button onClick={(e) => handleRemoveMember(member.user_id, e)} className="p-1 text-[#8C8C89] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="font-mono text-[10px] text-[#8C8C89] mt-2 text-center">Click member to switch</p>
            </div>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-9">
            {/* Period Selector */}
            <div className="flex gap-1 p-1 bg-[#F5F3EF] border border-[rgba(0,0,0,0.08)] mb-3" data-testid="period-selector">
              {["day", "week", "month"].map((period) => (
                <button key={period} onClick={() => handlePeriodChange(period)} className={`flex-1 py-2 font-mono text-xs transition-all ${leaderboardPeriod === period ? 'bg-[#D97706] text-white' : 'text-[#8C8C89] hover:text-[#5C5C5A]'}`} data-testid={`period-${period}`}>
                  {period === "day" ? "TODAY" : period === "week" ? "WEEK" : "MONTH"}
                </button>
              ))}
            </div>

            {/* Date Navigator */}
            <div className="flex items-center justify-between mb-4 px-1" data-testid="date-navigator">
              <button onClick={() => handleDateNav(-1)} data-testid="date-nav-prev" className="p-1.5 text-[#8C8C89] hover:text-[#D97706] transition-colors"><ChevronLeft className="w-4 h-4" /></button>
              <span className="font-mono text-xs text-[#5C5C5A]">
                {selectedDay ? new Date(selectedDay + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : dateRange.label}
              </span>
              <button onClick={() => handleDateNav(1)} data-testid="date-nav-next" className="p-1.5 text-[#8C8C89] hover:text-[#D97706] transition-colors"><ChevronRight className="w-4 h-4" /></button>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-[#F5F3EF] border border-[rgba(0,0,0,0.08)] p-1 mb-4">
                <TabsTrigger value="leaderboard" className="font-mono text-xs data-[state=active]:bg-[#D97706] data-[state=active]:text-white text-[#8C8C89]">
                  <Trophy className="w-4 h-4 mr-2" />LEADERBOARDS
                </TabsTrigger>
                <TabsTrigger value="compare" className="font-mono text-xs data-[state=active]:bg-[#D97706] data-[state=active]:text-white text-[#8C8C89]">
                  <BarChart3 className="w-4 h-4 mr-2" />COMPARE
                </TabsTrigger>
                <TabsTrigger value="stats" className="font-mono text-xs data-[state=active]:bg-[#D97706] data-[state=active]:text-white text-[#8C8C89]">
                  <TrendingUp className="w-4 h-4 mr-2" />STATS
                </TabsTrigger>
              </TabsList>

              {/* Leaderboards + Activity Tab */}
              <TabsContent value="leaderboard" className="mt-0 space-y-4">
                {/* Leaderboards */}
                {Object.keys(workoutLeaderboards).length === 0 ? (
                  <div className="retro-box p-8 text-center">
                    <Trophy className="w-10 h-10 mx-auto mb-3 text-[#8C8C89]" />
                    <p className="font-mono text-sm text-[#5C5C5A]">NO DATA YET</p>
                    <p className="font-mono text-xs text-[#8C8C89] mt-1">Log some workouts to see leaderboards</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {Object.entries(workoutLeaderboards).map(([workoutName, data]) => (
                      <div key={workoutName} className="retro-box p-4">
                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[rgba(0,0,0,0.08)]">
                          {data.type === "reps" ? <Hash className="w-4 h-4 text-[#D97706]" /> : <Timer className="w-4 h-4 text-[#D97706]" />}
                          <span className="font-['VT323'] text-lg text-[#D97706]">{workoutName}</span>
                        </div>
                        {data.leaderboard.length === 0 ? (
                          <p className="font-mono text-xs text-[#8C8C89] text-center py-4">No data</p>
                        ) : (
                          <div className="space-y-2">
                            {data.leaderboard.slice(0, 5).map((entry, idx) => (
                              <div key={entry.user_id} className={`p-2 flex items-center justify-between border ${idx === 0 ? 'rank-gold' : idx === 1 ? 'rank-silver' : idx === 2 ? 'rank-bronze' : 'border-[rgba(0,0,0,0.08)] bg-[#FAF9F7]'}`}>
                                <div className="flex items-center gap-2">
                                  <div className={`w-6 h-6 flex items-center justify-center font-['VT323'] text-xl ${idx === 0 ? 'text-yellow-600' : idx === 1 ? 'text-gray-500' : idx === 2 ? 'text-orange-600' : 'text-[#8C8C89]'}`}>{entry.rank}</div>
                                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: getUserColor(entry.user_id) }} />
                                  <span className="font-mono text-base" style={{ color: getUserColor(entry.user_id) }}>{entry.username}</span>
                                </div>
                                <div className="font-['VT323'] text-2xl" style={{ color: getUserColor(entry.user_id) }}>
                                  {data.type === "time" ? secsToHMS(entry.total) : entry.total}<span className="text-sm text-[#8C8C89] ml-1">{data.type === "reps" ? "reps" : ""}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Activity Calendar */}
                <div className="retro-box p-4" data-testid="activity-calendar">
                  <div className="flex items-center gap-2 mb-4 pb-2 border-b border-[rgba(0,0,0,0.08)]">
                    <Calendar className="w-4 h-4 text-[#D97706]" />
                    <span className="font-mono text-xs uppercase text-[#8C8C89]">ACTIVITY</span>
                    <span className="font-mono text-xs text-[#8C8C89] ml-auto">{activities.length} logs</span>
                    {selectedDay && (
                      <button onClick={() => { setSelectedDay(null); fetchActivitiesForPeriod(); }} className="font-mono text-[10px] text-[#D97706] hover:underline" data-testid="show-all-btn">SHOW ALL</button>
                    )}
                  </div>

                  {/* MONTH: Full Calendar Grid */}
                  {leaderboardPeriod === "month" && !selectedDay && (
                    <MonthGrid refDate={refDate} calendarData={calendarData} userColorMap={userColorMap} getUserColor={getUserColor} onDayClick={handleDayClick} selectedDay={selectedDay} />
                  )}

                  {/* WEEK: 7-day columns */}
                  {leaderboardPeriod === "week" && !selectedDay && (
                    <WeekGrid refDate={refDate} activities={activities} getUserColor={getUserColor} onDayClick={handleDayClick} />
                  )}

                  {/* DAY or selected day: Detailed log list */}
                  {(leaderboardPeriod === "day" || selectedDay) && (
                    <DayDetail
                      activities={activities}
                      getUserColor={getUserColor}
                      userId={userId}
                      isOwner={isOwner}
                      editingLog={editingLog}
                      editValue={editValue}
                      editDate={editDate}
                      editOriginalDate={editOriginalDate}
                      setEditingLog={setEditingLog}
                      setEditValue={setEditValue}
                      setEditDate={setEditDate}
                      setEditOriginalDate={setEditOriginalDate}
                      handleEditLog={handleEditLog}
                      handleDeleteLog={handleDeleteLog}
                      addLogOpen={addLogOpen}
                      setAddLogOpen={setAddLogOpen}
                      addLogWorkout={addLogWorkout}
                      setAddLogWorkout={setAddLogWorkout}
                      addLogValue={addLogValue}
                      setAddLogValue={setAddLogValue}
                      addLogDate={addLogDate}
                      setAddLogDate={setAddLogDate}
                      handleAddLogForDay={handleAddLogForDay}
                      workouts={workouts}
                      selectedDay={selectedDay}
                    />
                  )}
                </div>
              </TabsContent>

              {/* Compare Tab */}
              <TabsContent value="compare" className="mt-0">
                <div className="retro-box p-6">
                  <h3 className="font-['VT323'] text-2xl text-[#D97706] mb-4">USER COMPARISON</h3>
                  {comparisonData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={comparisonData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                        <XAxis dataKey="username" tick={{ fontSize: 13, fontFamily: 'JetBrains Mono', fill: '#8C8C89' }} />
                        <YAxis tick={{ fontSize: 13, fontFamily: 'JetBrains Mono', fill: '#8C8C89' }} />
                        <Tooltip contentStyle={{ background: 'white', border: '1px solid rgba(217,119,6,0.3)', fontFamily: 'JetBrains Mono', fontSize: '13px' }} />
                        <Legend wrapperStyle={{ fontFamily: 'JetBrains Mono', fontSize: '13px' }} />
                        {workouts.map((workout, idx) => (
                          <Bar key={workout.name} dataKey={workout.name} fill={USER_COLORS[idx % USER_COLORS.length]} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="font-mono text-[#8C8C89] text-center py-12">NO DATA TO COMPARE</p>
                  )}
                </div>
              </TabsContent>

              {/* Stats Tab */}
              <TabsContent value="stats" className="mt-0">
                {!statsData ? (
                  <div className="retro-box p-8 text-center">
                    <TrendingUp className="w-10 h-10 mx-auto mb-3 text-[#8C8C89]" />
                    <p className="font-mono text-sm text-[#5C5C5A]">NO STATS DATA</p>
                    <p className="font-mono text-xs text-[#8C8C89] mt-1">Log some workouts to see stats</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Stats Sub-View Switcher */}
                    <div className="flex gap-1 p-1 bg-[#F5F3EF] border border-[rgba(0,0,0,0.08)]" data-testid="stats-view-selector">
                      {[
                        { id: "overview", label: "OVERVIEW", icon: BarChart3 },
                        { id: "trend", label: "DAILY TREND", icon: TrendingUp },
                        { id: "head2head", label: "HEAD TO HEAD", icon: Users },
                      ].map(({ id, label, icon: Icon }) => (
                        <button key={id} onClick={() => setStatsView(id)} className={`flex-1 py-1.5 font-mono text-xs flex items-center justify-center gap-1.5 transition-all ${statsView === id ? "bg-[#D97706] text-white" : "text-[#8C8C89] hover:text-[#5C5C5A]"}`} data-testid={`stats-view-${id}`}>
                          <Icon className="w-3.5 h-3.5" />{label}
                        </button>
                      ))}
                    </div>

                    {/* Overview */}
                    {statsView === "overview" && (
                      <div className="space-y-4">
                        <div className="retro-box p-4" data-testid="stats-rankings-table">
                          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[rgba(0,0,0,0.08)]">
                            <Trophy className="w-4 h-4 text-[#D97706]" />
                            <span className="font-mono text-xs uppercase text-[#8C8C89]">RANKINGS</span>
                          </div>
                          {statsData.comparison.length === 0 ? (
                            <p className="font-mono text-sm text-[#8C8C89] text-center py-6">NO DATA YET</p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full" data-testid="stats-comparison-table">
                                <thead>
                                  <tr className="border-b border-[rgba(0,0,0,0.08)]">
                                    <th className="text-left font-mono text-xs text-[#8C8C89] py-2 px-1.5">#</th>
                                    <th className="text-left font-mono text-xs text-[#8C8C89] py-2 px-1.5">USER</th>
                                    {statsData.workouts.map(w => (
                                      <th key={w.name} className="text-right font-mono text-xs text-[#8C8C89] py-2 px-1.5">{w.name}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {statsData.comparison.map((row, idx) => (
                                    <tr key={row.user_id} className={`border-b border-[rgba(0,0,0,0.04)] ${idx === 0 ? "rank-gold" : idx === 1 ? "rank-silver" : idx === 2 ? "rank-bronze" : ""}`} data-testid={`stats-row-${row.username}`}>
                                      <td className="py-2 px-1.5">
                                        <span className={`font-['VT323'] text-lg ${idx === 0 ? "text-yellow-600" : idx === 1 ? "text-gray-500" : idx === 2 ? "text-orange-600" : "text-[#8C8C89]"}`}>{idx + 1}</span>
                                      </td>
                                      <td className="py-2 px-1.5">
                                        <div className="flex items-center gap-1.5">
                                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: getUserColor(row.user_id) }} />
                                          <span className="font-mono text-sm" style={{ color: getUserColor(row.user_id) }}>{row.username}</span>
                                        </div>
                                      </td>
                                      {statsData.workouts.map(w => (
                                        <td key={w.name} className="text-right py-2 px-1.5">
                                          <span className="font-['VT323'] text-lg text-[#1F1F1E]">{w.type === "time" ? secsToHMS(row[w.name] || 0) : (row[w.name] || 0)}</span>
                                          <span className="font-mono text-[10px] text-[#8C8C89] ml-0.5">{w.type === "reps" ? "r" : ""}</span>
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          {statsData.workouts.map((workout) => {
                            const chartData = statsData.comparison
                              .map(c => ({ username: c.username, value: c[workout.name] || 0, user_id: c.user_id }))
                              .filter(d => d.value > 0)
                              .sort((a, b) => b.value - a.value);
                            if (chartData.length === 0) return null;
                            return (
                              <div key={workout.name} className="retro-box p-4" data-testid={`stats-workout-chart-${workout.name}`}>
                                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-[rgba(0,0,0,0.08)]">
                                  {workout.type === "reps" ? <Hash className="w-3 h-3 text-[#D97706]" /> : <Timer className="w-3 h-3 text-[#D97706]" />}
                                  <span className="font-['VT323'] text-base text-[#D97706]">{workout.name}</span>
                                </div>
                                <ResponsiveContainer width="100%" height={160}>
                                  <BarChart data={chartData} layout="vertical" margin={{ left: 5, right: 15 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                    <XAxis type="number" tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#8C8C89" }} />
                                    <YAxis dataKey="username" type="category" tick={{ fontSize: 12, fontFamily: "JetBrains Mono", fill: "#8C8C89" }} width={70} />
                                    <Tooltip contentStyle={{ background: "white", border: "1px solid rgba(217,119,6,0.3)", fontFamily: "JetBrains Mono", fontSize: "13px" }} formatter={(val) => [`${val} ${workout.type === "reps" ? "reps" : "sec"}`, workout.name]} />
                                    <Bar dataKey="value" radius={[0, 2, 2, 0]}>
                                      {chartData.map((entry, i) => (
                                        <Cell key={i} fill={getUserColor(entry.user_id)} />
                                      ))}
                                    </Bar>
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Daily Trend */}
                    {statsView === "trend" && (
                      <div className="space-y-4">
                        <div className="retro-box p-4" data-testid="stats-daily-trend">
                          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[rgba(0,0,0,0.08)]">
                            <TrendingUp className="w-4 h-4 text-[#D97706]" />
                            <span className="font-mono text-xs uppercase text-[#8C8C89]">DAILY ACTIVITY</span>
                          </div>
                          {statsData.daily_trend.length === 0 ? (
                            <p className="font-mono text-sm text-[#8C8C89] text-center py-8">NO DAILY DATA</p>
                          ) : (
                            <ResponsiveContainer width="100%" height={300}>
                              <LineChart data={statsData.daily_trend} margin={{ top: 5, right: 15, bottom: 5, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#8C8C89" }} tickFormatter={(d) => d.slice(5)} />
                                <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#8C8C89" }} />
                                <Tooltip contentStyle={{ background: "white", border: "1px solid rgba(217,119,6,0.3)", fontFamily: "JetBrains Mono", fontSize: "13px" }} />
                                <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: "12px" }} />
                                {statsData.comparison.map((c) => (
                                  <Line key={c.username} type="monotone" dataKey={c.username} stroke={getUserColor(c.user_id)} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                                ))}
                              </LineChart>
                            </ResponsiveContainer>
                          )}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {statsData.comparison.map((user, idx) => {
                            const summary = statsData.user_summaries[user.user_id];
                            if (!summary) return null;
                            const color = getUserColor(user.user_id);
                            return (
                              <div key={user.user_id} className="retro-box p-3" data-testid={`stats-user-card-${user.username}`}>
                                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-[rgba(0,0,0,0.08)]">
                                  <div className="w-7 h-7 border-2 flex items-center justify-center font-mono text-xs" style={{ borderColor: color, color }}>{user.username.slice(0, 2).toUpperCase()}</div>
                                  <span className="font-mono text-sm font-medium" style={{ color }}>{user.username}</span>
                                  {idx === 0 && <Trophy className="w-3 h-3 text-yellow-500 ml-auto" />}
                                </div>
                                <div className="space-y-0.5">
                                  {Object.entries(summary.workouts).map(([wn, wd]) => (
                                    <div key={wn} className="flex justify-between">
                                      <span className="font-mono text-[10px] text-[#5C5C5A]">{wn}</span>
                                      <span className="font-mono text-[10px] text-[#1F1F1E]">{wd.type === "time" ? secsToHMS(wd.total) : wd.total + " r"}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Head to Head */}
                    {statsView === "head2head" && statsData.comparison.length >= 2 && (() => {
                      const a = statsData.comparison.find(c => c.user_id === h2hUserA);
                      const b = statsData.comparison.find(c => c.user_id === h2hUserB);
                      const colorA = getUserColor(h2hUserA);
                      const colorB = getUserColor(h2hUserB);
                      const h2hData = statsData.workouts.map(w => ({ workout: w.name, [a?.username || "A"]: a?.[w.name] || 0, [b?.username || "B"]: b?.[w.name] || 0 }));
                      const aWins = statsData.workouts.filter(w => (a?.[w.name] || 0) > (b?.[w.name] || 0)).length;
                      const bWins = statsData.workouts.filter(w => (b?.[w.name] || 0) > (a?.[w.name] || 0)).length;
                      return (
                        <div className="space-y-4">
                          <div className="retro-box p-4" data-testid="stats-h2h-selector">
                            <div className="flex items-center gap-3 flex-wrap">
                              <select value={h2hUserA} onChange={(e) => setH2hUserA(e.target.value)} className="flex-1 min-w-[120px] h-9 bg-[#FAF9F7] border font-mono text-sm px-2 focus:outline-none" style={{ borderColor: colorA, color: colorA }} data-testid="stats-h2h-user-a">
                                {statsData.comparison.map(c => <option key={c.user_id} value={c.user_id}>{c.username}</option>)}
                              </select>
                              <span className="font-['VT323'] text-lg text-[#8C8C89]">VS</span>
                              <select value={h2hUserB} onChange={(e) => setH2hUserB(e.target.value)} className="flex-1 min-w-[120px] h-9 bg-[#FAF9F7] border font-mono text-sm px-2 focus:outline-none" style={{ borderColor: colorB, color: colorB }} data-testid="stats-h2h-user-b">
                                {statsData.comparison.map(c => <option key={c.user_id} value={c.user_id}>{c.username}</option>)}
                              </select>
                            </div>
                          </div>
                          {a && b && (
                            <div className="retro-box p-4" data-testid="stats-h2h-score">
                              <div className="flex items-center justify-center gap-6 py-3">
                                <div className="text-center">
                                  <div className="w-12 h-12 border-2 flex items-center justify-center font-mono text-sm mx-auto mb-1" style={{ borderColor: colorA, color: colorA }}>{a.username.slice(0, 2).toUpperCase()}</div>
                                  <div className="font-['VT323'] text-xl" style={{ color: colorA }}>{a.total}</div>
                                  <div className="font-mono text-[10px] text-[#8C8C89]">{a.username}</div>
                                </div>
                                <div className="text-center">
                                  <div className="font-['VT323'] text-3xl text-[#8C8C89]">{aWins} - {bWins}</div>
                                  <div className="font-mono text-[9px] text-[#8C8C89]">WORKOUT WINS</div>
                                </div>
                                <div className="text-center">
                                  <div className="w-12 h-12 border-2 flex items-center justify-center font-mono text-sm mx-auto mb-1" style={{ borderColor: colorB, color: colorB }}>{b.username.slice(0, 2).toUpperCase()}</div>
                                  <div className="font-['VT323'] text-xl" style={{ color: colorB }}>{b.total}</div>
                                  <div className="font-mono text-[10px] text-[#8C8C89]">{b.username}</div>
                                </div>
                              </div>
                            </div>
                          )}
                          {a && b && h2hData.length > 0 && (
                            <div className="retro-box p-4" data-testid="stats-h2h-chart">
                              <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={h2hData}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                  <XAxis dataKey="workout" tick={{ fontSize: 12, fontFamily: "JetBrains Mono", fill: "#8C8C89" }} />
                                  <YAxis tick={{ fontSize: 12, fontFamily: "JetBrains Mono", fill: "#8C8C89" }} />
                                  <Tooltip contentStyle={{ background: "white", border: "1px solid rgba(217,119,6,0.3)", fontFamily: "JetBrains Mono", fontSize: "13px" }} />
                                  <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: "12px" }} />
                                  <Bar dataKey={a.username} fill={colorA} />
                                  <Bar dataKey={b.username} fill={colorB} />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          )}
                          {a && b && (
                            <div className="space-y-2" data-testid="stats-h2h-breakdown">
                              {statsData.workouts.map(w => {
                                const aVal = a[w.name] || 0;
                                const bVal = b[w.name] || 0;
                                const maxVal = Math.max(aVal, bVal, 1);
                                const winner = aVal > bVal ? "a" : bVal > aVal ? "b" : "tie";
                                return (
                                  <div key={w.name} className="retro-box p-2.5">
                                    <div className="flex items-center justify-between mb-1.5">
                                      <span className="font-mono text-xs text-[#8C8C89]">{w.name}</span>
                                      <span className="font-mono text-[10px] text-[#8C8C89]">{w.type === "reps" ? "REPS" : "SEC"}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className={`font-['VT323'] text-base w-10 text-right`} style={{ color: winner === "a" ? colorA : "#8C8C89" }}>{aVal}</span>
                                      <div className="flex-1 flex h-4 overflow-hidden bg-[#F5F3EF]">
                                        <div className="transition-all" style={{ width: `${(aVal / maxVal) * 50}%`, backgroundColor: colorA }} />
                                        <div className="flex-1" />
                                        <div className="transition-all" style={{ width: `${(bVal / maxVal) * 50}%`, backgroundColor: colorB }} />
                                      </div>
                                      <span className={`font-['VT323'] text-base w-10`} style={{ color: winner === "b" ? colorB : "#8C8C89" }}>{bVal}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {statsView === "head2head" && statsData.comparison.length < 2 && (
                      <div className="retro-box p-8 text-center">
                        <Users className="w-10 h-10 mx-auto mb-3 text-[#8C8C89]" />
                        <p className="font-mono text-sm text-[#5C5C5A]">NEED 2+ USERS</p>
                        <p className="font-mono text-xs text-[#8C8C89] mt-1">Add more members to compare</p>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Add User Dialog */}
      <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
        <DialogContent className="bg-white border-[rgba(217,119,6,0.3)] max-w-md">
          <DialogHeader><DialogTitle className="font-['VT323'] text-2xl text-[#D97706]">ADD USER</DialogTitle></DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4 mt-4">
            <Input value={newUserName} onChange={(e) => setNewUserName(e.target.value)} placeholder="enter username..." className="h-11 bg-[#FAF9F7] border-[rgba(0,0,0,0.15)] text-[#D97706] font-mono" />
            <button type="submit" disabled={submitting} className="w-full h-11 retro-btn-solid font-mono text-sm">{submitting ? "ADDING..." : "[ADD USER]"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LobbyView;
